import { useState, useEffect } from 'react';
import {
  Users,
  Plus,
  Search,
  Filter,
  Eye,
  Mail,
  MessageSquare,
  Phone,
  Calendar,
  FileText,
  ExternalLink,
  Send,
  CheckCircle,
  Clock,
  AlertCircle,
  DollarSign,
  Home,
  UserPlus,
  Copy,
  RotateCcw,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Separator } from './ui/separator';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Client, Communication } from '../types/client';
import { Property } from '../types/property';
import { toast } from 'sonner';
import { logger } from '../services/logger.service';
import { clientAPI } from '../lib/client-api';
import { apiPost } from '../lib/api-response';
import { getFrontendDomain } from '@/config/runtime-config';

interface ClientManagementProps {
  onPropertySelect?: (property: Property) => void;
}

export function ClientManagement({ onPropertySelect }: ClientManagementProps) {
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isCreateClientOpen, setIsCreateClientOpen] = useState(false);
  const [isPreApprovalOpen, setIsPreApprovalOpen] = useState(false);
  const [isApplicationOpen, setIsApplicationOpen] = useState(false);
  const [isCommunicationOpen, setIsCommunicationOpen] = useState(false);
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingClient, setIsAddingClient] = useState(false);
  const [addClientError, setAddClientError] = useState('');
  const [newClientFirstName, setNewClientFirstName] = useState('');
  const [newClientLastName, setNewClientLastName] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setIsLoading(true);
      const fetchedClients = await clientAPI.getClients();
      
      setClients(fetchedClients);
    } catch (error) {
      logger.error('Failed to load clients', error as Error, undefined, 'ClientManagement');
      toast.error('Failed to load clients');
      setClients([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddClient = async () => {
    if (!newClientFirstName.trim() || !newClientLastName.trim()) {
      setAddClientError('First name and last name are required');
      return;
    }
    if (!newClientEmail.trim()) {
      setAddClientError('Email address is required');
      return;
    }
    if (!newClientPhone.trim()) {
      setAddClientError('Phone number is required');
      return;
    }
    setAddClientError('');
    setIsAddingClient(true);
    try {
      const created = await clientAPI.createClient({
        firstName: newClientFirstName.trim(),
        lastName: newClientLastName.trim(),
        email: newClientEmail.trim(),
        phone: newClientPhone.trim(),
      });
      setClients(prev => [created, ...prev]);
      setIsCreateClientOpen(false);
      setNewClientFirstName('');
      setNewClientLastName('');
      setNewClientEmail('');
      setNewClientPhone('');
      toast.success('Client added successfully');
    } catch (error: any) {
      const msg = error?.message || 'Failed to add client';
      setAddClientError(msg);
      toast.error(msg);
    } finally {
      setIsAddingClient(false);
    }
  };

  // Backend returns firstName + lastName; frontend type uses name. Support both.
  const clientDisplayName = (client: any): string =>
    client.name || `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'Unknown';

  const filteredClients = clients.filter(client => {
    const displayName = clientDisplayName(client);
    const matchesSearch = displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (client.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (client.phone || '').includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || client.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Lead': return 'bg-gray-100 text-gray-800';
      case 'Qualified': return 'bg-blue-100 text-blue-800';
      case 'Viewing': return 'bg-yellow-100 text-yellow-800';
      case 'Offer Made': return 'bg-orange-100 text-orange-800';
      case 'Under Contract': return 'bg-purple-100 text-purple-800';
      case 'Sold': return 'bg-green-100 text-green-800';
      case 'Inactive': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPreApprovalStatusIcon = (status: string) => {
    switch (status) {
      case 'Approved': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'In Progress': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'Declined': return <AlertCircle className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const handleSendPreApproval = (bank: string, amount: number) => {
    if (!selectedClient) return;

    const frontendDomain = getFrontendDomain();
    const link = `${frontendDomain}/pre-approval/${bank.toLowerCase()}?client=${selectedClient.id}&amount=${amount}`;

    toast.success(`Pre-approval link sent to ${clientDisplayName(selectedClient)} via ${bank}`);
    setIsPreApprovalOpen(false);
  };

  const handleSendUnifiedPreApproval = (method: 'whatsapp' | 'email' | 'copy') => {
    if (!selectedClient) return;

    // Generate unified pre-approval link to your application
    const frontendDomain = getFrontendDomain();
    const preApprovalLink = `${frontendDomain}/pre-approval?clientId=${selectedClient.id}&agentRef=AGENT001`;
    
    if (method === 'whatsapp') {
      const message = `Hi ${clientDisplayName(selectedClient)}, start your pre-approval journey here: ${preApprovalLink}. This will help us understand your budget and connect you with the right financing options.`;
      const whatsappUrl = `https://wa.me/${(selectedClient.phone ?? '').replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      toast.success('Pre-approval link sent via WhatsApp');
    } else if (method === 'email') {
      const subject = 'Start Your Pre-Approval Journey - Something Property';
      const body = `Hi ${clientDisplayName(selectedClient)},\n\nWe're excited to help you find your dream property! To get started, please complete your pre-approval application here:\n\n${preApprovalLink}\n\nThis secure application takes about 10-15 minutes to complete and will help us:\n- Understand your budget and affordability\n- Connect you with the best financing options\n- Fast-track your property search\n\nIf you have any questions, feel free to reach out.\n\nBest regards,\nSomething Property Team`;
      const emailUrl = `mailto:${selectedClient.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.open(emailUrl, '_blank');
      toast.success('Email client opened with pre-approval link');
    } else if (method === 'copy') {
      // Fallback method for copying text that works in all browsers
      const textArea = document.createElement('textarea');
      textArea.value = preApprovalLink;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        toast.success('Pre-approval link copied to clipboard');
      } catch (err) {
        toast.error('Failed to copy link. Please copy manually: ' + preApprovalLink);
      }
      
      document.body.removeChild(textArea);
    }
    
    setIsPreApprovalOpen(false);
  };

  const handleSendApplication = (type: string, method: 'whatsapp' | 'email' | 'link', propertyId?: string) => {
    if (!selectedClient) return;
    
    const bondApplicationLink = `${window.location.origin}/bond-application?client=${selectedClient.id}`;
    
    if (method === 'whatsapp') {
      const message = `Hi ${clientDisplayName(selectedClient)}, please complete your bond application here: ${bondApplicationLink}`;
      const whatsappUrl = `https://wa.me/${(selectedClient.phone ?? '').replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      toast.success(`Bond application link sent via WhatsApp to ${clientDisplayName(selectedClient)}`);
    } else if (method === 'email') {
      const subject = 'Complete Your Bond Application';
      const body = `Hi ${clientDisplayName(selectedClient)},%0D%0A%0D%0APlease complete your bond application using the following link:%0D%0A${bondApplicationLink}%0D%0A%0D%0AThank you,%0D%0ASomething Property`;
      window.location.href = `mailto:${selectedClient.email}?subject=${subject}&body=${body}`;
      toast.success(`Bond application email opened for ${clientDisplayName(selectedClient)}`);
    } else if (method === 'link') {
      // Copy link to clipboard
      const textArea = document.createElement('textarea');
      textArea.value = bondApplicationLink;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        toast.success('Bond application link copied to clipboard!');
      } catch (err) {
        toast.error('Failed to copy link. Please copy manually: ' + bondApplicationLink);
      }
      
      document.body.removeChild(textArea);
    }
    
    setIsApplicationOpen(false);
  };

  const handleSendCommunication = (type: string, message: string, subject?: string) => {
    if (!selectedClient) return;

    toast.success(`${type} sent to ${clientDisplayName(selectedClient)}`);
    setIsCommunicationOpen(false);
  };

  const handleSendEmail = async () => {
    if (!selectedClient) return;
    if (!emailSubject.trim() || !emailBody.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    setEmailSending(true);
    try {
      await apiPost('/agent-email/send', {
        to: selectedClient.email || '',
        toName: clientDisplayName(selectedClient),
        subject: emailSubject.trim(),
        body: emailBody.trim(),
        recipientId: selectedClient.id || (selectedClient as any).clientId,
        recipientType: 'client',
      });
      toast.success('Email sent');
      setIsEmailOpen(false);
      setEmailSubject('');
      setEmailBody('');
    } catch (error: any) {
      const msg = error?.message?.includes('SMTP not configured')
        ? 'Email not configured. Please connect your email in My Profile.'
        : error?.message || 'Failed to send email';
      toast.error(msg);
    } finally {
      setEmailSending(false);
    }
  };

  const handleRequestDocument = (label: string, docType: string) => {
    if (!selectedClient) return;
    const phone = ((selectedClient as any).phone ?? '').replace(/\D/g, '');
    const frontendDomain = getFrontendDomain();
    const clientId = selectedClient.id || (selectedClient as any).clientId || '';
    const msg = `Hi ${clientDisplayName(selectedClient)}, please upload your ${label} here: ${frontendDomain}/upload/client/${clientId}?type=${docType}`;
    if (phone) {
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
      toast.success(`${label} request sent via WhatsApp`);
    } else {
      navigator.clipboard.writeText(msg).catch(() => {});
      toast.success('Request link copied to clipboard');
    }
  };

  const clientStats = {
    total: [].length,
    active: [].filter(c => !['Sold', 'Inactive'].includes(c.status)).length,
    qualified: [].filter(c => c.status === 'Qualified').length,
    preApproved: [].filter(c => c.preApprovalStatus === 'Approved').length
  };

  if (selectedClient) {
    return (
      <div className="space-y-6">
        {/* Client Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => setSelectedClient(null)} data-testid="client-back-to-list">
              ← Back to Clients
            </Button>
            <div>
              <h3>{clientDisplayName(selectedClient)}</h3>
              <p className="text-muted-foreground">{selectedClient.email} • {selectedClient.phone}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Badge className={getStatusColor(selectedClient.status)}>
              {selectedClient.status}
            </Badge>
            <Dialog open={isPreApprovalOpen} onOpenChange={setIsPreApprovalOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="client-send-preapproval-trigger">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Send Pre-Approval
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Send Pre-Approval Link</DialogTitle>
                  <DialogDescription>
                    Send {clientDisplayName(selectedClient)} a link to complete their pre-approval journey
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <Card className="cursor-pointer hover:shadow-md transition-shadow" 
                        onClick={() => handleSendUnifiedPreApproval('whatsapp')}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <MessageSquare className="w-5 h-5 text-green-600" />
                          <div>
                            <h4 className="font-medium">Send via WhatsApp</h4>
                            <p className="text-sm text-muted-foreground">Quick message with pre-approval link</p>
                          </div>
                        </div>
                        <Button size="sm" data-testid="client-preapproval-whatsapp-send">Send</Button>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="cursor-pointer hover:shadow-md transition-shadow" 
                        onClick={() => handleSendUnifiedPreApproval('email')}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Mail className="w-5 h-5 text-blue-600" />
                          <div>
                            <h4 className="font-medium">Send via Email</h4>
                            <p className="text-sm text-muted-foreground">Professional email with instructions</p>
                          </div>
                        </div>
                        <Button size="sm" data-testid="client-preapproval-email-send">Send</Button>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => handleSendUnifiedPreApproval('copy')}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <ExternalLink className="w-5 h-5 text-purple-600" />
                          <div>
                            <h4 className="font-medium">Copy Link</h4>
                            <p className="text-sm text-muted-foreground">Copy to share via any channel</p>
                          </div>
                        </div>
                        <Button size="sm" data-testid="client-preapproval-copy">Copy</Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={isApplicationOpen} onOpenChange={setIsApplicationOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="client-send-bond-application-trigger">
                  <FileText className="w-4 h-4 mr-2" />
                  Send Bond Application
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Send Bond Application</DialogTitle>
                  <DialogDescription>
                    Send {clientDisplayName(selectedClient)} a bond application link
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <Card className="cursor-pointer hover:shadow-md transition-shadow" 
                        onClick={() => handleSendApplication('Bond Application', 'whatsapp')}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <MessageSquare className="w-5 h-5 text-green-600" />
                          <div>
                            <h4 className="font-medium">Send via WhatsApp</h4>
                            <p className="text-sm text-muted-foreground">Quick message with bond application link</p>
                          </div>
                        </div>
                        <Button size="sm" data-testid="client-bond-application-whatsapp-send">Send</Button>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => handleSendApplication('Bond Application', 'email')}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Mail className="w-5 h-5 text-blue-600" />
                          <div>
                            <h4 className="font-medium">Send via Email</h4>
                            <p className="text-sm text-muted-foreground">Professional email with bond application link</p>
                          </div>
                        </div>
                        <Button size="sm" data-testid="client-bond-application-email-send">Send</Button>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => handleSendApplication('Bond Application', 'link')}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <ExternalLink className="w-5 h-5 text-purple-600" />
                          <div>
                            <h4 className="font-medium">Copy Link</h4>
                            <p className="text-sm text-muted-foreground">Copy to share via any channel</p>
                          </div>
                        </div>
                        <Button size="sm" data-testid="client-bond-application-copy">Copy</Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </DialogContent>
            </Dialog>
            {/* F-112 — Send Email compose modal */}
            <Dialog open={isEmailOpen} onOpenChange={setIsEmailOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="client-send-email">
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Send Email to {clientDisplayName(selectedClient)}</DialogTitle>
                  <DialogDescription>
                    Compose an email using your connected email account
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>To</Label>
                    <Input value={(selectedClient as any).email || ''} disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Subject</Label>
                    <Input
                      placeholder="Email subject"
                      value={emailSubject}
                      onChange={e => setEmailSubject(e.target.value)}
                      disabled={emailSending}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Message</Label>
                    <Textarea
                      placeholder="Type your message..."
                      rows={5}
                      value={emailBody}
                      onChange={e => setEmailBody(e.target.value)}
                      disabled={emailSending}
                    />
                  </div>
                  <Button
                    className="w-full"
                    onClick={handleSendEmail}
                    disabled={emailSending}
                    data-testid="client-send-email-submit"
                  >
                    {emailSending ? 'Sending…' : 'Send Email'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={isCommunicationOpen} onOpenChange={setIsCommunicationOpen}>
              <DialogTrigger asChild>
                <Button data-testid="client-contact-trigger">
                  <Send className="w-4 h-4 mr-2" />
                  Contact Client
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Contact {clientDisplayName(selectedClient)}</DialogTitle>
                  <DialogDescription>
                    Send a message via your preferred channel
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Communication Type</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                        <SelectItem value="sms">SMS</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Subject (for email)</Label>
                    <Input placeholder="Email subject" />
                  </div>
                  <div className="space-y-2">
                    <Label>Message</Label>
                    <Textarea placeholder="Type your message..." rows={4} />
                  </div>
                  <Button onClick={() => handleSendCommunication('Email', 'Sample message')} className="w-full" data-testid="client-send-message">
                    Send Message
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Client Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    {getPreApprovalStatusIcon(selectedClient.preApprovalStatus)}
                    <div>
                      <p className="text-sm text-muted-foreground">Pre-Approval</p>
                      <p className="font-medium">{selectedClient.preApprovalStatus}</p>
                      {selectedClient.preApprovalAmount && (
                        <p className="text-xs text-muted-foreground">
                          R{selectedClient.preApprovalAmount.toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <DollarSign className="w-4 h-4 text-green-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Budget Range</p>
                      <p className="font-medium">
                        R{(selectedClient.budget?.min ?? 0).toLocaleString()} - R{(selectedClient.budget?.max ?? 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <Home className="w-4 h-4 text-blue-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Interested Properties</p>
                      <p className="font-medium">{(selectedClient.interestedProperties?.length ?? 0)} properties</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tabs for detailed info */}
            <Tabs defaultValue="activity" className="space-y-4">
              <TabsList>
                <TabsTrigger value="activity">Activity</TabsTrigger>
                <TabsTrigger value="properties">Properties ({selectedClient.interestedProperties?.length ?? 0})</TabsTrigger>
                <TabsTrigger value="applications">Applications ({selectedClient.applications?.length ?? 0})</TabsTrigger>
                <TabsTrigger value="communications">Communications ({selectedClient.communications?.length ?? 0})</TabsTrigger>
              </TabsList>

              <TabsContent value="activity">
                <Card>
                  <CardHeader>
                    <CardTitle>Recent Activity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {(selectedClient.communications ?? []).slice(0, 5).map((comm) => (
                        <div key={comm.id} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                          <div className="flex-1">
                            <p className="font-medium">{comm.type}: {comm.subject || comm.message.substring(0, 50)}...</p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(comm.sentAt).toLocaleDateString()} • {comm.direction}
                            </p>
                          </div>
                          <Badge variant="outline">{comm.status}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="properties">
                <Card>
                  <CardHeader>
                    <CardTitle>Interested Properties</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {(selectedClient.interestedProperties ?? []).map((propertyId) => {
                        const property = [].find(p => p.id === propertyId);
                        if (!property) return null;
                        
                        return (
                          <Card key={propertyId} className="cursor-pointer hover:shadow-md"
                                onClick={() => onPropertySelect?.(property)}>
                            <CardContent className="p-4">
                              <div className="flex items-center space-x-3">
                                <img
                                  src={property.media[0]?.url}
                                  alt={property.title}
                                  className="w-16 h-16 rounded-lg object-cover"
                                />
                                <div className="flex-1">
                                  <h4 className="font-medium">{property.title}</h4>
                                  <p className="text-sm text-muted-foreground">
                                    {property.address.suburb} • R{(property.price || 0).toLocaleString()}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {property.facts.bedrooms}bed • {property.facts.bathrooms}bath
                                  </p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="applications">
                <Card>
                  <CardHeader>
                    <CardTitle>Applications & Documents</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {(selectedClient.applications ?? []).map((app) => (
                        <div key={app.id} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h4 className="font-medium">{app.type}</h4>
                              <p className="text-sm text-muted-foreground">
                                Sent: {new Date(app.sentAt).toLocaleDateString()}
                                {app.completedAt && ` • Completed: ${new Date(app.completedAt).toLocaleDateString()}`}
                              </p>
                            </div>
                            <Badge variant={app.status === 'Completed' ? 'default' : 'outline'}>
                              {app.status}
                            </Badge>
                          </div>
                          {app.documents.length > 0 && (
                            <div>
                              <p className="text-sm font-medium mb-2">Documents:</p>
                              <div className="space-y-1">
                                {app.documents.map((doc) => (
                                  <div key={doc.id} className="flex items-center space-x-2">
                                    <FileText className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-sm">{doc.name}</span>
                                    <Badge variant="outline" className="text-xs">{doc.type}</Badge>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="communications">
                <Card>
                  <CardHeader>
                    <CardTitle>Communication History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {(selectedClient.communications ?? []).map((comm) => (
                        <div key={comm.id} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              {comm.type === 'Email' && <Mail className="w-4 h-4" />}
                              {comm.type === 'WhatsApp' && <MessageSquare className="w-4 h-4" />}
                              {comm.type === 'Call' && <Phone className="w-4 h-4" />}
                              <span className="font-medium">{comm.type}</span>
                              <Badge variant="outline">{comm.direction}</Badge>
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {new Date(comm.sentAt).toLocaleDateString()}
                            </div>
                          </div>
                          {comm.subject && (
                            <p className="font-medium mb-1">{comm.subject}</p>
                          )}
                          <p className="text-sm text-muted-foreground">{comm.message}</p>
                          <div className="flex items-center justify-between mt-2">
                            <Badge variant="outline" className="text-xs">{comm.status}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Client Info Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Client Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <Label className="text-sm text-muted-foreground">Client ID</Label>
                    <p className="font-mono text-xs text-muted-foreground truncate">{selectedClient.id || selectedClient.clientId}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 flex-shrink-0"
                    onClick={() => {
                      navigator.clipboard.writeText(selectedClient.id || selectedClient.clientId || '');
                      toast.success('Client ID copied');
                    }}
                    title="Copy Client ID"
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
                <Separator />
                <div>
                  <Label className="text-sm text-muted-foreground">Source</Label>
                  <p>{selectedClient.source}</p>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Created</Label>
                  <p>{selectedClient.createdAt ? new Date(selectedClient.createdAt).toLocaleDateString() : '—'}</p>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Last Contact</Label>
                  <p>{selectedClient.lastContact ? new Date(selectedClient.lastContact).toLocaleDateString() : '—'}</p>
                </div>
                <Separator />
                <div>
                  <Label className="text-sm text-muted-foreground">Property Preferences</Label>
                  <div className="mt-2 space-y-2">
                    <div className="flex flex-wrap gap-1">
                      {(selectedClient.preferences?.propertyTypes ?? []).map((type) => (
                        <Badge key={type} variant="outline" className="text-xs">{type}</Badge>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(selectedClient.preferences?.areas ?? []).map((area) => (
                        <Badge key={area} variant="outline" className="text-xs">{area}</Badge>
                      ))}
                    </div>
                    {selectedClient.preferences?.bedrooms && (
                      <p className="text-sm">{selectedClient.preferences.bedrooms}+ bedrooms</p>
                    )}
                  </div>
                </div>
                <Separator />
                <div>
                  <Label className="text-sm text-muted-foreground">Notes</Label>
                  <p className="text-sm mt-1">{selectedClient.notes}</p>
                </div>
              </CardContent>
            </Card>

            {/* F-132 — Identity Document request section */}
            <Card data-testid="client-documents-section">
              <CardHeader>
                <CardTitle className="text-sm">Identity Documents</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-muted-foreground">Request documents from client via WhatsApp</p>
                {[
                  { label: 'ID Document', type: 'idDocument' },
                  { label: 'Bank Statement', type: 'bankStatement' },
                  { label: 'Proof of Residence', type: 'proofOfResidence' },
                ].map(doc => (
                  <div key={doc.type} className="flex items-center justify-between gap-2">
                    <span className="text-sm">{doc.label}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`request-link-${doc.type}`}
                      onClick={() => handleRequestDocument(doc.label, doc.type)}
                    >
                      Send Request Link
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3>Client Management</h3>
          <p className="text-muted-foreground">
            Manage your clients, track sales progress, and send applications
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="icon" onClick={loadClients} disabled={isLoading} data-testid="client-refresh-button" title="Refresh clients">
            <RotateCcw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          <Dialog open={isCreateClientOpen} onOpenChange={setIsCreateClientOpen}>
            <DialogTrigger asChild>
              <Button data-testid="client-add-trigger">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Client
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Client</DialogTitle>
              <DialogDescription>
                Create a new client profile to start tracking their property journey
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {addClientError && (
                <p className="text-sm text-red-600">{addClientError}</p>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name</Label>
                  <Input
                    placeholder="John"
                    value={newClientFirstName}
                    onChange={e => setNewClientFirstName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Name</Label>
                  <Input
                    placeholder="Smith"
                    value={newClientLastName}
                    onChange={e => setNewClientLastName(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Phone Number <span className="text-red-500">*</span></Label>
                <Input
                  placeholder="+27 82 123 4567"
                  value={newClientPhone}
                  onChange={e => setNewClientPhone(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Email Address <span className="text-red-500">*</span></Label>
                <Input
                  type="email"
                  placeholder="john.smith@email.com"
                  value={newClientEmail}
                  onChange={e => setNewClientEmail(e.target.value)}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Budget Min</Label>
                  <Input type="number" placeholder="1000000" />
                </div>
                <div className="space-y-2">
                  <Label>Budget Max</Label>
                  <Input type="number" placeholder="2000000" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Source</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="How did they find us?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Website">Website</SelectItem>
                    <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                    <SelectItem value="Email">Email</SelectItem>
                    <SelectItem value="Referral">Referral</SelectItem>
                    <SelectItem value="Walk-in">Walk-in</SelectItem>
                    <SelectItem value="Social Media">Social Media</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea placeholder="Additional notes about the client..." rows={3} />
              </div>
              <Button
                onClick={handleAddClient}
                disabled={isAddingClient}
                className="w-full"
                data-testid="client-add-submit"
              >
                {isAddingClient ? 'Adding...' : 'Add Client'}
              </Button>
            </div>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card data-testid="analytics-client-total-clients-card">
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Users className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold" data-testid="analytics-client-total-clients-count">{clientStats.total}</p>
                <p className="text-sm text-muted-foreground" data-testid="analytics-client-total-clients-label">Total Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="analytics-client-active-clients-card">
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold" data-testid="analytics-client-active-clients-count">{clientStats.active}</p>
                <p className="text-sm text-muted-foreground" data-testid="analytics-client-active-clients-label">Active Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="analytics-client-qualified-leads-card">
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <UserPlus className="w-8 h-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold" data-testid="analytics-client-qualified-leads-count">{clientStats.qualified}</p>
                <p className="text-sm text-muted-foreground" data-testid="analytics-client-qualified-leads-label">Qualified Leads</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="analytics-client-pre-approved-card">
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <DollarSign className="w-8 h-8 text-yellow-500" />
              <div>
                <p className="text-2xl font-bold" data-testid="analytics-client-pre-approved-count">{clientStats.preApproved}</p>
                <p className="text-sm text-muted-foreground" data-testid="analytics-client-pre-approved-label">Pre-Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <div className="flex items-center space-x-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search clients by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="Lead">Lead</SelectItem>
            <SelectItem value="Qualified">Qualified</SelectItem>
            <SelectItem value="Viewing">Viewing</SelectItem>
            <SelectItem value="Offer Made">Offer Made</SelectItem>
            <SelectItem value="Under Contract">Under Contract</SelectItem>
            <SelectItem value="Sold">Sold</SelectItem>
            <SelectItem value="Inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Clients Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Pre-Approval</TableHead>
              <TableHead>Budget Range</TableHead>
              <TableHead>Properties</TableHead>
              <TableHead>Last Contact</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredClients.map((client) => (
              <TableRow key={client.id}>
                <TableCell>
                  <div className="flex items-center space-x-3">
                    <Avatar>
                      <AvatarFallback>{clientDisplayName(client).split(' ').map((n: string) => n[0]).join('')}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{clientDisplayName(client)}</p>
                      <p className="text-sm text-muted-foreground">{client.email}</p>
                      <p className="text-xs text-muted-foreground">{client.phone}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={getStatusColor(client.status)}>
                    {client.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center space-x-2">
                    {getPreApprovalStatusIcon(client.preApprovalStatus)}
                    <span className="text-sm">{client.preApprovalStatus}</span>
                  </div>
                  {client.preApprovalAmount && (
                    <p className="text-xs text-muted-foreground">
                      R{client.preApprovalAmount.toLocaleString()}
                    </p>
                  )}
                </TableCell>
                <TableCell>
                  <div>
                    <p className="text-sm">R{(client.budget?.min ?? 0).toLocaleString()}</p>
                    <p className="text-sm">R{(client.budget?.max ?? 0).toLocaleString()}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {(client.interestedProperties?.length ?? 0)} properties
                  </Badge>
                </TableCell>
                <TableCell>
                  <p className="text-sm">{client.lastContact ? new Date(client.lastContact).toLocaleDateString() : '—'}</p>
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedClient(client)}
                    data-testid="client-view-details"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}